/**
 * Product video compression via browser MediaRecorder (canvas redraw).
 * ffmpeg.wasm hangs on many product camera clips here (0% until timeout).
 */

export const MAX_PRODUCT_VIDEO_BYTES = 10 * 1024 * 1024;

const mb = (bytes) => (bytes / (1024 * 1024)).toFixed(2);

const formatCompressionSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

const pickRecorderMime = () => {
  const candidates = [
    'video/webm;codecs=vp9,opus',
    'video/webm;codecs=vp9',
    'video/webm;codecs=vp8,opus',
    'video/webm;codecs=vp8',
    'video/webm',
  ];
  if (typeof MediaRecorder === 'undefined') return '';
  return candidates.find((t) => MediaRecorder.isTypeSupported(t)) || '';
};

const even = (n) => {
  const v = Math.max(2, Math.round(n));
  return v % 2 === 0 ? v : v + 1;
};

export const getVideoDuration = (file) =>
  new Promise((resolve) => {
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.onloadedmetadata = () => {
      URL.revokeObjectURL(video.src);
      resolve(video.duration || 10);
    };
    video.onerror = () => {
      URL.revokeObjectURL(video.src);
      resolve(10);
    };
    video.src = URL.createObjectURL(file);
  });

export const generateCanvasThumbnail = (file) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.muted = true;
    video.playsInline = true;
    video.src = url;

    let settled = false;
    const cleanup = () => {
      URL.revokeObjectURL(url);
      video.removeAttribute('src');
      video.load();
    };
    const fail = (err) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      cleanup();
      reject(err || new Error('Thumbnail failed'));
    };
    const succeed = (out) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      cleanup();
      resolve(out);
    };
    const timer = setTimeout(() => fail(new Error('Thumbnail timed out')), 8000);

    video.onerror = () => fail(new Error('Could not read video for thumbnail'));
    video.onloadeddata = () => {
      const seekTo = Math.min(0.4, Math.max(0.1, (video.duration || 1) * 0.05));
      const capture = () => {
        try {
          const w = 200;
          const h = even((video.videoHeight / (video.videoWidth || 1)) * w || 200);
          const canvas = document.createElement('canvas');
          canvas.width = w;
          canvas.height = h;
          canvas.getContext('2d').drawImage(video, 0, 0, w, h);
          canvas.toBlob(
            (blob) => {
              if (!blob) {
                fail(new Error('Thumbnail blob empty'));
                return;
              }
              succeed(
                new File(
                  [blob],
                  `thumb_${(file.name || 'video').replace(/\.[^/.]+$/, '')}.jpg`,
                  { type: 'image/jpeg', lastModified: Date.now() },
                ),
              );
            },
            'image/jpeg',
            0.82,
          );
        } catch (err) {
          fail(err);
        }
      };
      video.onseeked = capture;
      try {
        video.currentTime = seekTo;
      } catch (_) {
        capture();
      }
    };
  });

/**
 * Re-encode by playing into a ≤720p canvas + MediaRecorder.
 */
export const compressWithMediaRecorder = (videoFile, { onProgress } = {}) =>
  new Promise((resolve, reject) => {
    const mimeType = pickRecorderMime();
    if (!mimeType) {
      reject(new Error('This browser cannot compress video (MediaRecorder unsupported)'));
      return;
    }

    const url = URL.createObjectURL(videoFile);
    const video = document.createElement('video');
    video.preload = 'auto';
    video.muted = true;
    video.playsInline = true;
    video.src = url;

    let settled = false;
    let rafId = 0;
    let watchdog = null;
    let stream = null;

    const cleanup = () => {
      if (watchdog) clearTimeout(watchdog);
      cancelAnimationFrame(rafId);
      try {
        stream?.getTracks?.().forEach((t) => t.stop());
      } catch (_) {
        /* ignore */
      }
      try {
        video.pause();
      } catch (_) {
        /* ignore */
      }
      URL.revokeObjectURL(url);
      video.removeAttribute('src');
      video.load();
    };

    const fail = (err) => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(err instanceof Error ? err : new Error(String(err || 'Compression failed')));
    };

    const succeed = (blob) => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve(blob);
    };

    video.onerror = () => fail(new Error('Could not read video file'));

    video.onloadedmetadata = () => {
      (async () => {
        try {
          const duration =
            video.duration > 0 && Number.isFinite(video.duration) ? video.duration : 10;
          const srcW = video.videoWidth || 720;
          const srcH = video.videoHeight || 1280;
          const maxEdge = 720;
          const scale = Math.min(1, maxEdge / Math.max(srcW, srcH));
          const cw = even(srcW * scale);
          const ch = even(srcH * scale);

          const canvas = document.createElement('canvas');
          canvas.width = cw;
          canvas.height = ch;
          const ctx = canvas.getContext('2d', { alpha: false });

          stream = canvas.captureStream(30);
          try {
            const mediaStream =
              typeof video.captureStream === 'function'
                ? video.captureStream()
                : typeof video.mozCaptureStream === 'function'
                  ? video.mozCaptureStream()
                  : null;
            mediaStream?.getAudioTracks?.().forEach((track) => {
              stream.addTrack(track);
            });
          } catch (_) {
            /* video-only is fine */
          }

          const chunks = [];
          const targetBitrate = Math.min(
            1_500_000,
            Math.max(600_000, Math.floor((2.2 * 1024 * 1024 * 8) / duration)),
          );

          console.log(
            `▶ ${mb(videoFile.size)} MB | ${duration.toFixed(1)}s | canvas ${cw}x${ch} | MediaRecorder ${mimeType} @ ${Math.round(targetBitrate / 1000)}k`,
          );

          const recorder = new MediaRecorder(stream, {
            mimeType,
            videoBitsPerSecond: targetBitrate,
          });
          recorder.ondataavailable = (e) => {
            if (e.data && e.data.size > 0) chunks.push(e.data);
          };
          recorder.onerror = () => fail(new Error('MediaRecorder failed'));
          recorder.onstop = () => {
            const blob = new Blob(chunks, { type: mimeType.split(';')[0] });
            if (!blob.size) {
              fail(new Error('Compression produced an empty file'));
              return;
            }
            succeed(blob);
          };

          const draw = () => {
            if (settled) return;
            try {
              ctx.drawImage(video, 0, 0, cw, ch);
            } catch (_) {
              /* frame skip */
            }
            if (duration > 0) {
              onProgress?.(Math.min(90, Math.floor((video.currentTime / duration) * 90)));
            }
            if (!video.paused && !video.ended) {
              rafId = requestAnimationFrame(draw);
            }
          };

          video.onended = () => {
            onProgress?.(92);
            try {
              if (recorder.state !== 'inactive') recorder.stop();
            } catch (err) {
              fail(err);
            }
          };

          watchdog = setTimeout(() => {
            if (settled) return;
            console.warn('[Product Video] MediaRecorder watchdog — forcing stop');
            try {
              video.pause();
            } catch (_) {
              /* ignore */
            }
            try {
              if (recorder.state !== 'inactive') recorder.stop();
              else fail(new Error('Video compression timed out'));
            } catch (err) {
              fail(err);
            }
          }, Math.ceil(duration + 15) * 1000);

          await video.play();
          recorder.start(250);
          rafId = requestAnimationFrame(draw);
          onProgress?.(5);
        } catch (err) {
          fail(err);
        }
      })();
    };
  });

export function createProductVideoCompressor() {
  const processProductVideoFile = async (file, { onProgress } = {}) => {
    if (!file?.type?.startsWith('video/')) {
      throw new Error('Please select a valid video file');
    }
    if (file.size > MAX_PRODUCT_VIDEO_BYTES) {
      throw new Error('Video exceeds the 10MB limit');
    }

    const compressionStartedAt = Date.now();
    console.log(`[Product Video] Compressing: ${file.name} (${mb(file.size)} MB)`);
    onProgress?.(5);

    const compressedBlob = await compressWithMediaRecorder(file, { onProgress });
    const ext = (compressedBlob.type || '').includes('webm') ? '.webm' : '.mp4';
    const compressedFile = new File(
      [compressedBlob],
      file.name.replace(/\.[^/.]+$/, '') + ext,
      { type: compressedBlob.type || 'video/webm', lastModified: Date.now() },
    );

    const usedCompressedVideo = compressedFile.size < file.size;
    const finalVideo = usedCompressedVideo ? compressedFile : file;
    const outputMB = finalVideo.size / (1024 * 1024);
    const rangeReason = usedCompressedVideo
      ? (outputMB <= 10 ? 'under 10MB' : 'over 10MB')
      : 'output larger or already optimized';

    console.log(
      `[Product Video] | original: ${formatCompressionSize(file.size)} | final: ${formatCompressionSize(finalVideo.size)} | status: ${usedCompressedVideo ? 'compressed' : 'original kept'} | reason: ${rangeReason} | ${(Date.now() - compressionStartedAt) / 1000}s`,
    );

    if (finalVideo.size > MAX_PRODUCT_VIDEO_BYTES) {
      throw new Error('Compressed video still exceeds the 10MB limit');
    }

    onProgress?.(94);

    let thumbnailFile = null;
    let thumbnailUrl = '';
    try {
      thumbnailFile = await generateCanvasThumbnail(finalVideo);
      thumbnailUrl = URL.createObjectURL(thumbnailFile);
      console.log(
        `[Product Video Thumbnail] Generated | size: ${formatCompressionSize(thumbnailFile.size)}`,
      );
    } catch (thumbErr) {
      console.warn('[Product Video] Thumbnail skipped:', thumbErr);
    }

    onProgress?.(100);

    return {
      file: finalVideo,
      thumbnailFile,
      previewUrl: URL.createObjectURL(finalVideo),
      thumbnailUrl,
    };
  };

  return {
    processProductVideoFile,
    terminate: () => {},
  };
}
